import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaWallet, FaCoins, FaGift, FaTimes, FaStar, FaGem, FaCrown, FaTrophy, FaSignInAlt } from 'react-icons/fa';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import './LuxuryCashbackDisplay.css';

const LuxuryCashbackDisplay = ({ 
  orderTotal = 0, 
  position = 'bottom-right', // 'bottom-right', 'top-strip', 'product-ribbon'
  showFloatingIcon = true,
  onClose = null,
  autoOpen = false, // New prop to control auto-opening
  pageKey = 'home' // New prop to differentiate between pages
}) => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showShimmer, setShowShimmer] = useState(false);
  const [showCoinDrop, setShowCoinDrop] = useState(false);
  const [showSparkle, setShowSparkle] = useState(false);
  const [isVisible, setIsVisible] = useState(true);

  // Calculate potential credits (10% of order total)
  const potentialCredits = orderTotal > 0 ? Math.floor(orderTotal * 0.1) : null;
  const creditPercentage = 10;
  
  // Get display text for credits
  const getCreditsDisplayText = () => {
    if (potentialCredits && potentialCredits > 0) {
      return `₹${potentialCredits.toLocaleString()} Style Credits`;
    }
    return "Up to ₹2,000 Style Credits";
  };

  // Handle button click
  const handleButtonClick = () => {
    if (currentUser) {
      // User is logged in, navigate to collections
      navigate('/collections');
    } else {
      // User is not logged in, navigate to login
      navigate('/login');
    }
  };
  

  // Auto-open logic for first-time visitors
  useEffect(() => {
    if (autoOpen) {
      const storageKey = `luxury-cashback-seen-${pageKey}`;
      const hasSeenPopup = localStorage.getItem(storageKey);
      if (!hasSeenPopup) {
        // Delay auto-open to let page load
        const timer = setTimeout(() => {
          setIsExpanded(true);
          localStorage.setItem(storageKey, 'true');
        }, 2000);
        return () => clearTimeout(timer);
      }
    }
  }, [autoOpen, pageKey]);

  // Trigger shimmer animation when component mounts
  useEffect(() => {
    if (isExpanded) {
      setShowShimmer(true);
      setTimeout(() => setShowShimmer(false), 2000);
    }
  }, [isExpanded]);

  // Trigger coin drop animation
  const handleIconClick = () => {
    setShowCoinDrop(true);
    setTimeout(() => setShowCoinDrop(false), 1000);
    setIsExpanded(!isExpanded);
  };

  // Trigger sparkle animation on hover
  const handleIconHover = () => {
    setShowSparkle(true);
    setTimeout(() => setShowSparkle(false), 600);
  };

  // Close handler
  const handleClose = () => {
    setIsExpanded(false);
    if (onClose) {
      onClose();
    }
  };

  // Hide component
  const handleHide = () => {
    setIsVisible(false);
  };

  if (!isVisible) {
    return null;
  }

  // Different layouts based on position
  if (position === 'top-strip') {
    return (
      <motion.div 
        className="luxury-cashback-strip"
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -50 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        <div className="strip-content">
          <div className="strip-icon">
            <FaStar />
          </div>
          <span className="strip-text">
            You'll earn 10% Style Credits with this look
          </span>
          <div className="strip-credits">
            {potentialCredits ? `₹${potentialCredits.toLocaleString()}` : "Up to ₹2,000"}
          </div>
        </div>
        <button className="strip-close" onClick={handleHide}>
          <FaTimes />
        </button>
      </motion.div>
    );
  }

  if (position === 'product-ribbon') {
    return (
      <motion.div 
        className="luxury-cashback-ribbon"
        initial={{ opacity: 0, scale: 0.8 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <div className="ribbon-content">
          <div className="ribbon-icon">
            <FaCoins />
          </div>
          <span className="ribbon-text">10% Style Credits</span>
        </div>
      </motion.div>
    );
  }

  // Default floating position
  return (
    <>
      {/* Floating Icon */}
      {showFloatingIcon && (
        <motion.div 
          className="luxury-cashback-floating-icon"
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
        >
          <motion.button
            className="floating-icon-btn luxury-credit-card"
            onClick={handleIconClick}
            onMouseEnter={handleIconHover}
            whileHover={{ rotateY: 15 }}
            whileTap={{ scale: 0.95 }}
          >
            {/* Credit Card with Holographic Effect */}
            <motion.div 
              className="credit-card"
              animate={{ 
                y: [0, -2, 0],
                rotateY: [0, 5, -5, 0]
              }}
              transition={{ 
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            >
              <div className="card-chip">💳</div>
              <div className="card-number">•••• 1234</div>
              <div className="card-brand">Vastré</div>
              <div className="card-shine"></div>
            </motion.div>

            {/* Money Explosion Effect */}
            <div className="money-explosion">
              <motion.div 
                className="explosion-bill bill-1"
                animate={{ 
                  x: [0, 25, 0],
                  y: [0, -15, 0],
                  rotate: [0, 180, 360],
                  opacity: [0, 1, 0]
                }}
                transition={{ 
                  duration: 2.5,
                  repeat: Infinity,
                  ease: "easeOut",
                  delay: 0.2
                }}
              >
                💵
              </motion.div>
              <motion.div 
                className="explosion-bill bill-2"
                animate={{ 
                  x: [0, -20, 0],
                  y: [0, -20, 0],
                  rotate: [0, -90, -180],
                  opacity: [0, 1, 0]
                }}
                transition={{ 
                  duration: 2.8,
                  repeat: Infinity,
                  ease: "easeOut",
                  delay: 0.5
                }}
              >
                💵
              </motion.div>
              <motion.div 
                className="explosion-bill bill-3"
                animate={{ 
                  x: [0, 15, 0],
                  y: [0, -25, 0],
                  rotate: [0, 90, 180],
                  opacity: [0, 1, 0]
                }}
                transition={{ 
                  duration: 2.2,
                  repeat: Infinity,
                  ease: "easeOut",
                  delay: 0.8
                }}
              >
                💵
              </motion.div>
              <motion.div 
                className="explosion-bill bill-4"
                animate={{ 
                  x: [0, -25, 0],
                  y: [0, -10, 0],
                  rotate: [0, -180, -360],
                  opacity: [0, 1, 0]
                }}
                transition={{ 
                  duration: 2.6,
                  repeat: Infinity,
                  ease: "easeOut",
                  delay: 1.1
                }}
              >
                💵
              </motion.div>
            </div>

            {/* Floating Coins Around Card */}
            <div className="floating-coins">
              <motion.div 
                className="float-coin coin-1"
                animate={{ 
                  y: [0, -8, 0],
                  x: [0, 3, 0],
                  rotate: [0, 360, 720]
                }}
                transition={{ 
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
              >
                🪙
              </motion.div>
              <motion.div 
                className="float-coin coin-2"
                animate={{ 
                  y: [0, -6, 0],
                  x: [0, -2, 0],
                  rotate: [0, -180, -360]
                }}
                transition={{ 
                  duration: 2.8,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 0.7
                }}
              >
                🪙
              </motion.div>
              <motion.div 
                className="float-coin coin-3"
                animate={{ 
                  y: [0, -10, 0],
                  x: [0, 4, 0],
                  rotate: [0, 180, 360]
                }}
                transition={{ 
                  duration: 3.2,
                  repeat: Infinity,
                  ease: "easeInOut",
                  delay: 1.4
                }}
              >
                🪙
              </motion.div>
            </div>


            
            {/* Sparkle burst animation on hover */}
            <AnimatePresence>
              {showSparkle && (
                <motion.div
                  className="sparkle-burst"
                  initial={{ scale: 0, opacity: 1 }}
                  animate={{ scale: 1.5, opacity: 0 }}
                  exit={{ scale: 2, opacity: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                >
                  <FaStar />
                </motion.div>
              )}
            </AnimatePresence>

            {/* Coin drop animation */}
            <AnimatePresence>
              {showCoinDrop && (
                <motion.div
                  className="coin-drop"
                  initial={{ y: -20, opacity: 1, scale: 0.8 }}
                  animate={{ y: 20, opacity: 0, scale: 1.2 }}
                  exit={{ y: 40, opacity: 0, scale: 1.5 }}
                  transition={{ duration: 1, ease: "easeOut" }}
                >
                  <FaCoins />
                </motion.div>
              )}
            </AnimatePresence>
          </motion.button>
        </motion.div>
      )}

      {/* Expanded Card */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div 
            className="luxury-cashback-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={handleClose}
          >
            <motion.div
              className="luxury-cashback-card"
              initial={{ opacity: 0, scale: 0.8, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.8, y: 20 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Certificate Seal Design */}
              <div className="certificate-seal">
                <div className="seal-circle">
                  <div className="seal-percentage">{creditPercentage}%</div>
                  <div className="seal-label">Style Credits</div>
                </div>
              </div>

              {/* Shimmer Animation */}
              <AnimatePresence>
                {showShimmer && (
                  <motion.div
                    className="shimmer-effect"
                    initial={{ x: '-100%' }}
                    animate={{ x: '100%' }}
                    transition={{ duration: 2, ease: "easeInOut" }}
                  />
                )}
              </AnimatePresence>

              {/* Content */}
              <div className="cashback-content">
                <h3 className="cashback-title">
                  Your Style Wallet
                </h3>
                <p className="cashback-subtitle">
                  {currentUser 
                    ? "Every look adds to your Style Wallet — 10% back for your next fit."
                    : "This offer is only valid for logged-in users. Please login to earn Style Credits."
                  }
                </p>

                {/* Gamified Credit Meter */}
                <div className="credit-meter">
                  <div className="meter-header">
                    <div className="meter-label-section">
                      <span className="meter-label">You can earn</span>
                      <span className="meter-arrow">→</span>
                    </div>
                    <div className="meter-amount-section">
                      <span className="meter-amount">{getCreditsDisplayText()}</span>
                    </div>
                  </div>
                  <div className="meter-bar">
                    <motion.div 
                      className="meter-fill"
                      initial={{ width: 0 }}
                      animate={{ width: '100%' }}
                      transition={{ duration: 1.5, ease: "easeOut", delay: 0.5 }}
                    />
                    <div className="meter-glow"></div>
                  </div>
                  <div className="meter-footer">
                    <span>✨ Added to Wallet on Purchase</span>
                  </div>
                </div>

                {/* Call to Action */}
                <div className="cashback-cta">
                  <motion.button
                    className="luxury-cashback-cta-button"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleButtonClick}
                  >
                    {currentUser ? (
                      <>
                        <FaGift className="luxury-cashback-cta-icon" />
                        <span>Start Shopping</span>
                      </>
                    ) : (
                      <>
                        <FaSignInAlt className="luxury-cashback-cta-icon" />
                        <span>Login Now</span>
                      </>
                    )}
                  </motion.button>
                </div>
              </div>

              {/* Close Button */}
              <button className="card-close" onClick={handleClose}>
                <FaTimes />
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default LuxuryCashbackDisplay;
